// Bulandshahr — Home page. Southwestern-inspired composition:
// 1. Full-viewport hero with rotating headlines and CTA
// 2. Three-column quick-pitch strip
// 3. "I am a __ interested in __ and __ at MIT Bulandshahr" personalizer (signature block)
// 4. Recognition stat tiles
// 5. Alternating image/text feature rows (About · Academics · Campus · Placements)
// 6. Pull quote testimonial
// 7. Alumni / news feed
// 8. Departments preview
//
// All content pulls from CMS data via `useBul()`; missing data degrades gracefully.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBul } from "./BulLayout";
import { STATIC_CONTENT, slugify, Eyebrow, SectionHeading, PullQuote, StatCard, FeatureRow, FONT } from "./_shared"; // eslint-disable-line no-unused-vars
import { ArrowRight, ChevronDown, ChevronRight, Newspaper, Calendar } from "lucide-react";

// -------- Hero carousel — full viewport (image-only, no overlay text) --------

function HeroCarousel({ slides, inst }) {
  const [idx, setIdx] = useState(0);
  const list = (slides || []).filter((s) => s.image_url);
  const hasSlides = list.length > 0;

  useEffect(() => {
    if (list.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 7000);
    return () => clearInterval(t);
  }, [list.length]);

  const cur = list[idx];

  return (
    <section className="relative bg-[#0F0F10] text-white overflow-hidden" data-testid="bul-hero">
      <div className="relative w-full h-[78vh] min-h-[520px] max-h-[780px]">
        {hasSlides ? (
          list.map((s, i) => (
            <div
              key={s.id || i}
              className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
            >
              <img
                src={s.image_url}
                alt={s.title || inst?.name || ""}
                className="w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </div>
          ))
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F0F10] via-[#141312] to-[#2A2620]">
            {inst?.home_image_url && (
              <img
                src={inst.home_image_url}
                alt=""
                className="w-full h-full object-cover"
              />
            )}
          </div>
        )}
        {list.length > 1 && (
          <div className="absolute bottom-8 right-8 z-20 flex items-center gap-2">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Show slide ${i + 1}`}
                className={`h-[3px] transition-all ${i === idx ? "bg-[#FDBB30] w-10" : "bg-white/40 hover:bg-white/80 w-5"}`}
              />
            ))}
          </div>
        )}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center text-[#FDBB30] pointer-events-none">
          <div className="text-[9px] uppercase tracking-[0.4em] font-semibold">Scroll</div>
          <ChevronDown size={16} strokeWidth={2} className="mt-1 animate-bounce" />
        </div>
      </div>
      {/* screen-reader-only current-slide caption so the image doesn't feel invisible to assistive tech */}
      {cur?.title && <span className="sr-only" aria-live="polite">{cur.title}</span>}
    </section>
  );
}

// -------- Personalizer ("I am a ___ interested in ___ and ___ at MIT Bulandshahr")
const PERSONAS = [
  { key: "prospective_student", label: "prospective student", topics: ["admissions", "departments", "placements", "campus_life", "facilities", "fees", "alumni_network"] },
  { key: "parent", label: "parent", topics: ["admissions", "fees", "facilities", "placements", "campus_life", "alumni_network"] },
  { key: "current_student", label: "current student", topics: ["academics", "campus_life", "placements", "facilities", "alumni_network"] },
  { key: "alumnus", label: "alumnus / alumna", topics: ["alumni_network", "campus_life", "placements", "campus_engagements"] },
  { key: "recruiter", label: "recruiter", topics: ["placements", "departments", "academics", "campus_engagements"] },
];

const TOPIC_LABELS = {
  admissions: "admissions",
  departments: "departments",
  academics: "academics",
  placements: "placements",
  campus_life: "campus life",
  campus_engagements: "campus engagements",
  facilities: "facilities",
  fees: "fees",
  alumni_network: "alumni network",
};

const TOPIC_ROUTES = {
  admissions: "admissions",
  departments: "departments",
  academics: "academics",
  placements: "careers",
  campus_life: "campus-engagements",
  campus_engagements: "campus-engagements",
  facilities: "facilities",
  fees: "admissions",
  alumni_network: "alumni",
};

function Personalizer() {
  const navigate = useNavigate();
  const [persona, setPersona] = useState(PERSONAS[0].key);
  const [topicA, setTopicA] = useState("admissions");
  const [topicB, setTopicB] = useState("departments");

  const p = useMemo(() => PERSONAS.find((x) => x.key === persona) || PERSONAS[0], [persona]);
  const availableTopics = p.topics;

  useEffect(() => {
    if (!availableTopics.includes(topicA)) setTopicA(availableTopics[0]);
    if (!availableTopics.includes(topicB)) setTopicB(availableTopics[1] || availableTopics[0]);
  }, [persona, availableTopics, topicA, topicB]);

  const go = () => {
    // Prefer the first topic that has a dedicated route; open the primary path.
    const target = TOPIC_ROUTES[topicA] || TOPIC_ROUTES[topicB] || "admissions";
    navigate(target);
  };

  const SelectPill = ({ value, onChange, options, testid }) => (
    <span className="relative inline-block align-baseline">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="appearance-none bg-transparent border-b-2 border-[#FDBB30] text-[#0F0F10] font-semibold pr-6 pl-1 py-1 focus:outline-none cursor-pointer"
        style={{ fontFamily: FONT.serif, fontStyle: "italic", fontSize: "inherit" }}
      >
        {options.map((k) => (
          <option key={k} value={k}>{k in TOPIC_LABELS ? TOPIC_LABELS[k] : (PERSONAS.find(pp => pp.key === k)?.label || k)}</option>
        ))}
      </select>
      <ChevronDown size={13} strokeWidth={2} className="absolute right-0 top-1/2 -translate-y-1/2 text-[#FDBB30] pointer-events-none" />
    </span>
  );

  return (
    <section className="bg-[#EDE7DA] py-20 md:py-28 border-y border-[#D9D2C1]" data-testid="bul-personalizer">
      <div className="max-w-6xl mx-auto px-6">
        <Eyebrow>Personalise your visit</Eyebrow>
        <div
          className="mt-6 text-3xl md:text-5xl leading-[1.25] text-[#0F0F10]"
          style={{ fontFamily: FONT.serif, fontWeight: 400 }}
        >
          I am a{" "}
          <SelectPill
            value={persona}
            onChange={setPersona}
            options={PERSONAS.map((x) => x.key)}
            testid="bul-personalizer-persona"
          />
          {" "}interested in exploring information about{" "}
          <SelectPill
            value={topicA}
            onChange={setTopicA}
            options={availableTopics}
            testid="bul-personalizer-topic-a"
          />
          {" "}and{" "}
          <SelectPill
            value={topicB}
            onChange={setTopicB}
            options={availableTopics.filter((t) => t !== topicA)}
            testid="bul-personalizer-topic-b"
          />
          {" "}at MIT Bulandshahr.
        </div>
        <div className="mt-10">
          <button
            type="button"
            onClick={go}
            data-testid="bul-personalizer-go"
            className="inline-flex items-center gap-3 bg-[#0F0F10] hover:bg-[#1F1F20] text-white font-semibold uppercase tracking-[0.22em] text-[11px] px-8 py-4 transition-colors"
          >
            Customise This Page
            <ArrowRight size={14} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </section>
  );
}

// -------- Quick pitch strip (3 cards under hero) --------
const QUICK_PITCH = [
  { title: "Learn Without Limits", body: "Find your place in a world of ideas.", to: "academics", cta: "Learn more" },
  { title: "Your Four-Year Roadmap", body: "Explore, experience, and prepare for what's next with a clear, personalised path from day one.", to: "departments", cta: "Learn more" },
  { title: "Proven Career Outcomes", body: "Our graduates go on to leading recruiters, higher studies and entrepreneurship — from day one.", to: "careers", cta: "Learn more" },
];

function QuickPitch() {
  return (
    <section className="bg-[#F8F5EE] border-b border-[#D9D2C1]" data-testid="bul-quick-pitch">
      <div className="max-w-7xl mx-auto grid md:grid-cols-3">
        {QUICK_PITCH.map((q) => (
          <Link
            key={q.title}
            to={q.to}
            className="group relative p-10 md:p-12 border-r border-[#D9D2C1] last:border-r-0 hover:bg-[#EDE7DA] transition-colors"
            data-testid={`bul-quickpitch-${slugify(q.title)}`}
          >
            <h3
              className="text-2xl md:text-3xl text-[#0F0F10] leading-tight"
              style={{ fontFamily: FONT.serif, fontWeight: 600 }}
            >
              {q.title}
            </h3>
            <p className="mt-4 text-[#2A2A2B] leading-relaxed">{q.body}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-[#0F0F10] border-b-2 border-[#FDBB30] pb-1 group-hover:text-[#E19E14]">
              {q.cta} <ArrowRight size={13} strokeWidth={2.4} />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// -------- News feed --------
function NewsFeed({ notices = [], events = [] }) {
  const items = [
    ...notices.slice(0, 4).map((n) => ({ id: `n-${n.id}`, kind: "News", title: n.title, date: n.date, icon: Newspaper, to: n.file_url || "notices", external: !!n.file_url })),
    ...events.slice(0, 3).map((e) => ({ id: `e-${e.id}`, kind: "Event", title: e.title, date: e.start_date || e.date, icon: Calendar, to: `campus-engagements` })),
  ];
  if (items.length === 0) return null;
  return (
    <section className="bg-[#F8F5EE] py-16 md:py-20 border-b border-[#D9D2C1]" data-testid="bul-news-feed">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Eyebrow>Newsroom</Eyebrow>
            <SectionHeading className="mt-4">Latest from campus</SectionHeading>
          </div>
          <Link to="notices" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-[#0F0F10] border-b-2 border-[#FDBB30] pb-1 hover:text-[#E19E14]">
            All News <ArrowRight size={13} strokeWidth={2.4} />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, 6).map((n) => {
            const Icon = n.icon;
            const inner = (
              <>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FDBB30] font-semibold flex items-center gap-2">
                  <Icon size={12} strokeWidth={2} />{n.kind}
                </div>
                <h3
                  className="mt-3 text-xl leading-snug text-[#0F0F10]"
                  style={{ fontFamily: FONT.serif, fontWeight: 600 }}
                >
                  {n.title}
                </h3>
                {n.date && (
                  <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-[#726C61]">
                    {new Date(n.date + (n.date.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                )}
                <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0F0F10] group-hover:text-[#E19E14]">
                  Read more <ChevronRight size={12} strokeWidth={2.4} />
                </div>
              </>
            );
            return n.external ? (
              <a key={n.id} href={n.to} target="_blank" rel="noopener noreferrer"
                 className="group bg-white p-7 border-t-4 border-[#FDBB30] hover:shadow-lg transition-shadow" data-testid={`bul-news-${n.id}`}>
                {inner}
              </a>
            ) : (
              <Link key={n.id} to={n.to} className="group bg-white p-7 border-t-4 border-[#FDBB30] hover:shadow-lg transition-shadow" data-testid={`bul-news-${n.id}`}>
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// -------- Departments strip --------
function DepartmentsStrip({ departments = [] }) {
  const list = departments.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).slice(0, 8);
  if (list.length === 0) return null;
  return (
    <section className="bg-white py-16 md:py-20 border-b border-[#D9D2C1]" data-testid="bul-departments-strip">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Eyebrow>Programmes</Eyebrow>
            <SectionHeading className="mt-4">Explore our departments</SectionHeading>
          </div>
          <Link to="departments" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-[#0F0F10] border-b-2 border-[#FDBB30] pb-1 hover:text-[#E19E14]">
            All Departments <ArrowRight size={13} strokeWidth={2.4} />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {list.map((d) => (
            <Link
              key={d.id}
              to={`departments/${slugify(d.name)}`}
              className="group bg-[#F8F5EE] hover:bg-[#0F0F10] hover:text-white transition-colors p-6 border-l-4 border-[#FDBB30]"
              data-testid={`bul-dept-${d.id}`}
            >
              {d.code && (
                <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#726C61] group-hover:text-[#FDBB30]">
                  {d.code}
                </div>
              )}
              <div
                className="mt-2 text-lg leading-snug text-[#0F0F10] group-hover:text-white"
                style={{ fontFamily: FONT.serif, fontWeight: 600 }}
              >
                {d.name}
              </div>
              <div className="mt-4 text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0F0F10] group-hover:text-[#FDBB30] inline-flex items-center gap-1">
                Discover <ChevronRight size={12} strokeWidth={2.4} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const data = useBul();
  const inst = data.institute || {};
  const notices = data.notices_general || [];
  const events = data.events || [];
  const departments = data.departments || [];
  const cmsBlocks = Array.isArray(inst.home_feature_blocks) ? inst.home_feature_blocks : [];

  // Fallback content when the CMS hasn't been populated yet. Three blocks in
  // the same order as the CMS so admins can overwrite them one-by-one.
  const defaultBlocks = [
    {
      eyebrow: "Academics",
      title: "For decades, MIT has been a destination for academic rigour.",
      body: "Our faculty are known for a high level of scholarship and a serious dedication to teaching and close collaboration with students. Departments offer industry-aligned degree programmes designed to prepare graduates for success in any field.",
      image_url: "",
      cta_label: "Majors & Programmes",
      cta_href: "departments",
    },
    {
      eyebrow: "Experience MIT",
      title: "What is life here really like? A campus visit shows you more than a beautiful campus.",
      body: "Attend a class, talk with our students and professors, tour our labs and library. You will find a friendly, driven community that daily celebrates the excitement of learning and discovery.",
      image_url: "",
      cta_label: "Schedule a visit",
      cta_href: "contact",
    },
    {
      eyebrow: "Fees & Financial Aid",
      title: "We are committed to making higher education accessible.",
      body: "Through merit scholarships and need-based financial aid, MIT Bulandshahr helps every student make an investment in their future — regardless of background.",
      image_url: "",
      cta_label: "Know your fees",
      cta_href: "admissions",
    },
  ];
  // Merge CMS content over the defaults so any missing field keeps its default.
  const featureBlocks = defaultBlocks.map((d, i) => ({ ...d, ...(cmsBlocks[i] || {}) }));

  return (
    <div data-testid="bul-home" className="bg-[#F8F5EE]">
      <HeroCarousel slides={data.header_slides} inst={inst} />
      <QuickPitch />

      {/* Recognition stat tiles */}
      <section className="bg-[#F8F5EE] py-16 md:py-20" data-testid="bul-recognition">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <StatCard
            number="AICTE Approved · AKTU Affiliated"
            label="Recognition"
            source="Statutory Bodies · Government of India"
            testid="bul-stat-aicte"
          />
          <StatCard
            number="Industry-Aligned Curriculum"
            label="Outcomes"
            source="MIT Group · Est. 1975"
            testid="bul-stat-outcomes"
          />
        </div>
      </section>

      <Personalizer />

      {/* Editorial feature rows (CMS-driven for Bulandshahr) */}
      {featureBlocks.map((b, i) => {
        const href = (b.cta_href || "").trim();
        const isExternal = /^https?:\/\//i.test(href);
        const cta = href
          ? {
              label: b.cta_label || "Learn more",
              as: isExternal ? "a" : Link,
              ...(isExternal ? { href, target: "_blank", rel: "noopener noreferrer" } : { to: href }),
              testid: `bul-feature-cta-${i}`,
            }
          : null;
        return (
          <FeatureRow
            key={`f-${i}`}
            eyebrow={b.eyebrow}
            title={b.title}
            body={b.body}
            image={b.image_url}
            flip={i % 2 === 1}
            cta={cta}
            testid={`bul-feature-cms-${i}`}
          />
        );
      })}

      {/* Testimonial — gold band, ink text */}
      <section className="bg-[#FDBB30] text-[#0F0F10] py-20 md:py-28" data-testid="bul-testimonial">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-[#0F0F10] font-semibold">
            <span className="inline-block w-8 h-[2px] bg-[#0F0F10]" />
            <span>Proven Outcomes</span>
          </div>
          <blockquote className="mt-6 relative" data-testid="bul-pull-quote">
            <span
              aria-hidden="true"
              className="absolute -top-8 -left-2 text-[110px] leading-none text-[#0F0F10]/25 select-none pointer-events-none"
              style={{ fontFamily: FONT.serif }}
            >
              &ldquo;
            </span>
            <p
              className="text-2xl md:text-4xl leading-snug italic text-[#0F0F10] relative"
              style={{ fontFamily: FONT.serif }}
            >
              MIT helped me challenge myself, explore my passions, and learn what I was truly capable of. It gave me the community and the environment that nurtured my creativity and enabled me to flourish.
            </p>
            <footer className="mt-6 text-[11px] uppercase tracking-[0.3em] text-[#0F0F10] font-semibold">
              — MIT Alumnus · Class of ’19
            </footer>
          </blockquote>
        </div>
      </section>

      <DepartmentsStrip departments={departments} />
      <NewsFeed notices={notices} events={events} />

      {/* Final CTA — big paper band with pull quote from Vision */}
      <section className="bg-[#EDE7DA] py-20 md:py-28 border-y border-[#D9D2C1]" data-testid="bul-vision-cta">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Eyebrow>Vision · Mission</Eyebrow>
          <div className="mt-6 flex justify-center">
            <PullQuote quote={(inst.vision || "").replace(/<[^>]+>/g, " ").trim() || STATIC_CONTENT.vision} />
          </div>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Link
              to="about"
              className="inline-flex items-center gap-2 bg-[#0F0F10] hover:bg-[#1F1F20] text-white font-semibold uppercase tracking-[0.22em] text-[11px] px-8 py-4"
              data-testid="bul-vision-cta-btn"
            >
              Read Our Story <ArrowRight size={13} strokeWidth={2.4} />
            </Link>
            <Link
              to="vision-mission"
              className="inline-flex items-center gap-2 border-b-2 border-[#0F0F10] hover:border-[#FDBB30] text-[#0F0F10] font-semibold uppercase tracking-[0.22em] text-[11px] pb-1"
            >
              Vision &amp; Mission <ArrowRight size={13} strokeWidth={2.4} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
